<template>
    <div v-if="additionals">
        <div v-if="additionals.salaVacinacaoLogada && !!additionals.salaVacinacaoLogada.id">
            <rkm-data-table
                :fields="fields"
                :repository="repositories.paciente"
                :request-params="requestParams"
                :disabled-button-search="true"
                :tbody-tr-class="rowClass"
                ref="RkmDataTable"
                sort-by="nome"
            >
                <template v-slot:cell(nome)="{ item }">
                    {{ item.nome }}

                    <template v-if="item.nomeSocial">
                        <template v-if="item.preferenciaNomeId === enums.preferenciaNome.NOME_SOCIAL">
                            <strong>({{ item.nomeSocial }})</strong>
                        </template>

                        <template v-else>
                            ({{ item.nomeSocial }})
                        </template>
                    </template>
                </template>

                <template v-slot:cell(dataNascimento)="row">
                    {{ formatDate(row.value) }}
                </template>

                <template v-slot:cell(cpf)="row">
                    {{ formatCpf(row.value) }}
                </template>

                <template v-slot:cell(cns)="row">
                    {{ formatCns(row.value) }}
                </template>

                <template v-slot:cell(actions)="row">
                    <rkm-button
                        @click="handleEdit(row.item)"
                        authorize="store-imunizacao-registro-vacinacao"
                        color="primary"
                        small
                        title="Visualizar"
                    >
                        <rkm-icon icon="search" />
                    </rkm-button>
                </template>
            </rkm-data-table>

            <rkm-row class="mt-3">
                <rkm-col>
                    <p class="m-0">
                        Linhas em <span class="danger">vermelho</span> representam pacientes em óbito.
                    </p>
                </rkm-col>
            </rkm-row>
        </div>

        <rkm-alert v-else color="warning">
            A unidade logada não é uma sala de vacinação, favor cadastrá-la para efetuar um registro de vacinação.
        </rkm-alert>
    </div>
</template>

<script>
// Enums.
import preferenciaNome from '@/enums/cadastro/cidadao/preferenciaNome'
import subclassificacao from '@/enums/cadastro/paciente/subclassificacao'
import classificacao from '@/enums/cadastro/paciente/classificacao'

// Mixins.
import { error, formats, permissao, repository, stringCase } from '@/mixins'

// Vuex.
import { mapActions } from 'vuex'

export default {
    computed: {
        fields() {
            const fields = [
                { key: 'nome', label: 'Nome', name: 'cadastroCidadao.nome', searchable: true, sortable: true },
                { key: 'dataNascimento', label: 'Data de nascimento', name: 'cadastroCidadao.dataNascimento', searchable: true, sortable: true },
                { key: 'nomeMae', label: 'Nome da mãe', name: 'cadastroCidadao.nomeMae', searchable: true, sortable: true },
                { key: 'prontuario', label: 'Prontuário municipal', name: 'cadastroPaciente.prontuario', searchable: true, sortable: true },
                { key: 'cpf', label: 'CPF', name: 'cadastroCidadao.cpf', searchable: true, sortable: true },
                { key: 'cns', label: 'CNS', name: 'cadastroCidadao.cns', searchable: true, sortable: true },
            ]

            if (this.verificarPermissao('create-imunizacao-registro-vacinacao')) {
                fields.push({ class: 'col-min text-center', key: 'actions', label: 'Ações' })

            }

            return fields
        },
        requestParams() {

            return { with_paciente_bloqueado: true,  with_paciente_provisorio: true }
        },
    },
    created() {
        this.repositories = {
            paciente: this.getConectaSusRepository('cadastro.sujeitoAtencao.paciente'),
            registroVacinacao: this.getConectaSusRepository('imunizacao.registroVacinacao'),
        }
    },
    data: () => ({
        additionals: null,
        enums: { preferenciaNome, subclassificacao, classificacao },
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {
            this.setLoading()

            this.repositories.registroVacinacao.index()
                .then(response => {
                    this.additionals = Object.assign(response.data.additionals)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleEdit(item) {

            this.setLoading()

            this.repositories.paciente.validCNS(this.camelObjectKeysToSnakeRecursive({pacienteId: item.id}))
                .then(response => {

                    if (!response.data.additionals.cnsValido && response.data.additionals.avisaCnsTemporarioInvalido) {

                        this.showWarningNotification('Paciente com CNS inválido. Registro não permitido.')
                        return false
                    }

                    if (item.subclassificacaoId === this.enums.subclassificacao.TEMPORARIO
                        && response.data.additionals.avisaCnsTemporarioInvalido) {

                            const confirm = async () => {

                                this.$router.push({ name: 'imunizacao-registro-vacinacao-create', params: { id: item.id } })

                                return false
                            }

                            const msg = `Paciente com cadastro temporário. Deseja continuar?`

                            this.showConfirmation(msg, confirm)

                            return false
                    }

                    if (item.obitoEm) {

                            const confirm = async () => {

                                this.$router.push({ name: 'imunizacao-registro-vacinacao-create', params: { id: item.id } })

                                return false
                            }

                            const msg = `Paciente com óbito registrado. Deseja continuar?`

                            this.showConfirmation(msg, confirm)

                            return false
                    }

                    if (item.bloqueado) {

                        const confirm = async () => {

                            this.$router.push({ name: 'imunizacao-registro-vacinacao-create', params: { id: item.id } })

                            return false
                        }

                        if (this.additionals.parametrizacao.cadastroBloqueado) {

                            const msg = `Paciente está com o seu cadastro bloqueado. Deseja continuar?`
                            this.showConfirmation(msg, confirm)
                        } else {

                            const msg = `Paciente está com o seu cadastro bloqueado. O registro da aplicação não
                                será possível, apenas a atualização da carteirinha. Para realizar o registro, é
                                necessário desbloquear o cadastro. Deseja continuar para a tela de registro?`
                            this.showConfirmation(msg, confirm)
                        }

                        return false
                    }

                    if (item.classificacaoId == this.enums.classificacao.PROVISORIO) {

                        const confirm = async () => {

                            this.$router.push({ name: 'imunizacao-registro-vacinacao-create', params: { id: item.id } })

                            return false
                        }

                        if (item.diasCadastroDesatualizado > 0) {

                            if (this.additionals.parametrizacao.cadastroProvisorio) {

                                const msg = `Paciente está com o seu cadastro "classificação provisória" e com cadastro desatualizado há
                                    <strong>${item.diasCadastroDesatualizado}</strong> dia(s). Deseja continuar?`
                                this.showConfirmation(msg, confirm)
                            } else {

                                const msg = `Paciente está com o seu cadastro "classificação provisória" e com cadastro desatualizado há
                                    <strong>${item.diasCadastroDesatualizado}</strong> dia(s). O registro da aplicação não
                                    será possível, apenas a atualização da carteirinha. Para realizar o registro, é
                                    necessário atualizar o cadastro e ajustar a classificação do paciente.
                                    Deseja continuar para a tela de registro?`
                                this.showConfirmation(msg, confirm)
                            }
                        } else {

                            if (this.additionals.parametrizacao.cadastroProvisorio) {

                                const msg = `Paciente está com o seu cadastro "classificação provisória". Deseja continuar?`
                                this.showConfirmation(msg, confirm)
                            } else {

                                const msg = `Paciente está com o seu cadastro "classificação provisória". O registro da
                                    aplicação não será possível, apenas a atualização da carteirinha. Para realizar o
                                    registro, é necessário atualizar o cadastro e ajustar a classificação do paciente.
                                    Deseja continuar para a tela de registro?`
                                this.showConfirmation(msg, confirm)
                            }
                        }

                        return false
                    }

                    if (item.classificacaoId == this.enums.classificacao.OUTRO_MUNICIPIO) {

                        const confirm = async () => {

                            this.$router.push({ name: 'imunizacao-registro-vacinacao-create', params: { id: item.id } })

                            return false
                        }

                        if (item.diasCadastroDesatualizado > 0) {

                            if (this.additionals.parametrizacao.cadastroOutroMunicipio) {

                                const msg = `Paciente está com o seu cadastro "outro município" e com cadastro desatualizado há
                                    <strong>${item.diasCadastroDesatualizado}</strong> dia(s). Deseja continuar?`
                                this.showConfirmation(msg, confirm)
                            } else {

                                const msg = `Paciente está com o seu cadastro "outro município" e com cadastro desatualizado há
                                    <strong>${item.diasCadastroDesatualizado}</strong> dia(s). O registro da aplicação não
                                    será possível, apenas a atualização da carteirinha. Para realizar o registro, é
                                    necessário atualizar o cadastro indicando que o paciente pertence a este município.
                                    Deseja continuar para a tela de registro?`
                                this.showConfirmation(msg, confirm)
                            }
                        } else {

                            if (this.additionals.parametrizacao.cadastroOutroMunicipio) {

                                const msg = `Paciente está com o seu cadastro "outro município". Deseja continuar?`
                                this.showConfirmation(msg, confirm)
                            } else {

                                const msg = `Paciente está com o seu cadastro "outro município". O registro da aplicação não
                                    será possível, apenas a atualização da carteirinha. Para realizar o registro, é
                                    necessário atualizar o cadastro indicando que o paciente pertence a este município.
                                    Deseja continuar para a tela de registro?`
                                this.showConfirmation(msg, confirm)
                            }
                        }

                        return false
                    }

                    if (item.diasCadastroDesatualizado > 0) {

                        const confirm = async () => {

                            this.$router.push({ name: 'imunizacao-registro-vacinacao-create', params: { id: item.id } })

                            return false
                        }

                        if (this.additionals.parametrizacao.cadastroOutroMunicipio) {

                            const msg = `Paciente está com o seu cadastro desatualizado há
                                <strong>${item.diasCadastroDesatualizado}</strong> dia(s). Deseja continuar?`
                            this.showConfirmation(msg, confirm)
                        } else {

                            const msg = `Paciente está com o seu cadastro desatualizado há <strong>${item.diasCadastroDesatualizado}</strong>
                                dia(s). O registro da aplicação não será possível, apenas a atualização da carteirinha.
                                Para realizar o registro, é necessário atualizar o cadastro. Deseja continuar para a
                                tela de registro?`
                            this.showConfirmation(msg, confirm)
                        }

                        return false
                    }

                    this.$router.push({ name: 'imunizacao-registro-vacinacao-create', params: { id: item.id } })
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        rowClass(item, type) {
            if(item && type === 'row') {
                if (item.obitoEm) {
                    return 'table-danger'
                }
            }
        },
    },
    mixins: [ error, formats, permissao, repository, stringCase ],
    mounted() {
        this.load()
    },
}
</script>